import LegalShell from "@/pages/LegalShell";

export default function Privacy() {
  return (
    <LegalShell kind="privacy" title="Privacy Policy" updated="Last updated: 30 April 2026 · Effective: 30 April 2026">
      <p>
        This Privacy Policy explains how Zoku ("we", "us" or "our") collects, uses, stores and
        shares your personal information when you use the Zoku mobile application and website at
        zoku.in (the "Service"). We are committed to protecting your privacy and handling your
        data with care and transparency.
      </p>

      <h2>1. Information We Collect</h2>
      <h3>Information you provide directly</h3>
      <ul>
        <li>Account details: name, email address and password when you register.</li>
        <li>Profile information: age, height, weight, dietary goals and preferences you set within the app.</li>
        <li>Food and product logs: meals you log, products you scan, photos you upload.</li>
        <li>Support communications: messages you send to support@zoku.in.</li>
      </ul>
      <h3>Information collected automatically</h3>
      <ul>
        <li>Usage data: features used, scans performed, pages visited, session duration.</li>
        <li>Device information: device type, operating system, app version, unique device identifiers.</li>
        <li>Log data: IP address, browser type, referring URLs, error reports and crash logs.</li>
        <li>Location data: approximate country or region inferred from your IP address. We do not collect precise GPS location.</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide, operate and maintain the Service.</li>
        <li>Personalise your experience, including calorie goals, ingredient alerts and product recommendations.</li>
        <li>Process and display your scanned product results and nutritional data.</li>
        <li>Send you transactional emails, such as account verification and password resets.</li>
        <li>Respond to your support requests and enquiries.</li>
        <li>Analyse usage patterns to improve the Service and fix bugs.</li>
        <li>Comply with applicable legal obligations.</li>
      </ul>
      <p>
        We do not sell your personal information to third parties. We do not use your data to
        serve you advertisements.
      </p>

      <h2>3. Legal Basis for Processing (UK & EU Users)</h2>
      <p>
        If you are located in the United Kingdom or European Economic Area, our legal bases for
        processing your personal data are:
      </p>
      <ul>
        <li><strong>Contract:</strong> processing necessary to provide the Service you have signed up for.</li>
        <li><strong>Legitimate interests:</strong> improving the Service, ensuring security, and preventing fraud.</li>
        <li><strong>Consent:</strong> where you have given explicit consent, such as for optional analytics.</li>
        <li><strong>Legal obligation:</strong> where we are required to process data by law.</li>
      </ul>

      <h2>4. Sharing Your Information</h2>
      <ul>
        <li><strong>Service providers:</strong> third-party companies that help us operate the Service, including cloud hosting, analytics and email delivery. These providers are contractually required to protect your data and may only use it to provide services to us.</li>
        <li><strong>AI processing partners:</strong> ingredient and nutritional analysis may be processed by AI services. No personally identifiable information is shared with these services — only anonymised product data.</li>
        <li><strong>Legal authorities:</strong> where required by law, court order or government request.</li>
        <li><strong>Business transfers:</strong> in the event of a merger, acquisition or sale of assets, your information may be transferred as part of that transaction.</li>
      </ul>

      <h2>5. Data Retention</h2>
      <p>
        We retain your personal data for as long as your account is active or as needed to provide
        the Service. If you delete your account, we will delete or anonymise your personal data
        within 30 days, except where we are required to retain it for legal or regulatory reasons.
      </p>

      <h2>6. Your Rights</h2>
      <ul>
        <li><strong>Access:</strong> request a copy of the personal data we hold about you.</li>
        <li><strong>Correction:</strong> request that we correct inaccurate or incomplete data.</li>
        <li><strong>Deletion:</strong> request that we delete your personal data ("right to be forgotten").</li>
        <li><strong>Portability:</strong> request your data in a structured, machine-readable format.</li>
        <li><strong>Objection:</strong> object to processing based on legitimate interests.</li>
        <li><strong>Restriction:</strong> request that we restrict processing of your data in certain circumstances.</li>
        <li><strong>Withdraw consent:</strong> where processing is based on consent, withdraw it at any time.</li>
      </ul>
      <p>
        To exercise any of these rights, please contact us at{" "}
        <a href="mailto:support@zoku.in">support@zoku.in</a>. We will respond within 30 days.
      </p>

      <h2>7. Cookies</h2>
      <p>
        Our website uses cookies and similar tracking technologies to enhance your experience.
        Essential cookies are required for the Service to function. Optional analytics cookies
        help us understand how the Service is used. You can manage cookie preferences through your
        browser settings at any time.
      </p>

      <h2>8. Children's Privacy</h2>
      <p>
        The Service is not directed at children under 13. We do not knowingly collect personal
        information from children under 13. If you believe we have inadvertently collected such
        information, please contact us immediately and we will delete it promptly.
      </p>

      <h2>9. International Data Transfers</h2>
      <p>
        Your information may be transferred to and processed in countries other than your own,
        including countries that may not have the same data protection laws. Where we transfer
        data outside the UK or EEA, we ensure appropriate safeguards are in place, such as
        standard contractual clauses approved by the relevant authorities.
      </p>

      <h2>10. Security</h2>
      <p>
        We implement industry-standard technical and organisational measures to protect your
        personal data against unauthorised access, loss, destruction or alteration. However, no
        method of transmission over the internet or electronic storage is 100% secure, and we
        cannot guarantee absolute security.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of material
        changes by updating the "Last updated" date and, where appropriate, by in-app notification
        or email. Your continued use of the Service after any changes constitutes your acceptance
        of the updated policy.
      </p>

      <h2>12. Contact & Complaints</h2>
      <p>
        If you have any questions, concerns or complaints about this Privacy Policy or our data
        practices, please contact us at <a href="mailto:support@zoku.in">support@zoku.in</a>.
      </p>
      <p>
        If you are located in the UK and are not satisfied with our response, you have the right
        to lodge a complaint with the Information Commissioner's Office (ICO) at{" "}
        <a href="https://ico.org.uk/" target="_blank" rel="noreferrer">
          ico.org.uk
        </a>
        .
      </p>
    </LegalShell>
  );
}
